// src/pages/common/ui/Modal.tsx
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | 'auto' | 'full';

export default function Modal({
    open,
    onClose,
    title,
    children,
    footer,
    size = 'md',
    closeOnBackdrop = true,
    closeOnEsc = true,
    showCloseButton = true,
    initialFocusRef,
    'aria-labelledby': ariaLabelledby,
}: {
    open: boolean;
    onClose: () => void;
    title?: React.ReactNode;
    children?: React.ReactNode;
    footer?: React.ReactNode;
    size?: ModalSize;
    closeOnBackdrop?: boolean;
    closeOnEsc?: boolean;
    showCloseButton?: boolean;
    initialFocusRef?: React.RefObject<HTMLElement>;
    'aria-labelledby'?: string;
}) {
    const panelRef = useRef<HTMLDivElement>(null);
    const lastFocused = useRef<HTMLElement | null>(null);
    const onCloseRef = useRef(onClose);

    useEffect(() => {
        if (!open) return;
        lastFocused.current = document.activeElement as HTMLElement | null;
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        const toFocus = initialFocusRef?.current ?? panelRef.current;
        setTimeout(() => toFocus?.focus(), 0);

        const onKey = (e: KeyboardEvent) => {
            if (closeOnEsc && e.key === 'Escape') {
                e.preventDefault();
                onCloseRef.current();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = originalOverflow;
            lastFocused.current?.focus?.();
        };
    }, [open, closeOnEsc, initialFocusRef]);

    if (!open) return null;

    // 폭 정책
    // - auto: 내용폭 기준(w-auto), 단 최대 96vw/80rem 제한
    // - full: 모바일/프리뷰용 거의 전체 화면
    const sizeClass: Record<ModalSize, string> = {
        sm: 'max-w-sm w-full',
        md: 'max-w-md w-full',
        lg: 'max-w-lg w-full',
        xl: 'max-w-xl w-full',
        '2xl': 'max-w-2xl w-full',
        '3xl': 'max-w-3xl w-full',
        '4xl': 'max-w-4xl w-full',
        '5xl': 'max-w-5xl w-full',
        '6xl': 'max-w-6xl w-full',
        auto: 'w-auto max-w-[96vw] max-w-[min(96vw,80rem)]',
        full: 'w-[96vw] max-w-[96vw]', // 거의 전체폭
    };

    const handleBackdrop = (e: React.MouseEvent) => {
        if (!closeOnBackdrop) return;
        if (e.target === e.currentTarget) onCloseRef.current();
    };

    return createPortal(
        <div
            className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4"
            onMouseDown={handleBackdrop}
            aria-modal="true"
            role="dialog"
            aria-labelledby={ariaLabelledby}
        >
            <div
                ref={panelRef}
                tabIndex={-1}
                className={`rounded-xl bg-white shadow-xl outline-none ${sizeClass[size]} max-h-[85vh] overflow-hidden flex flex-col`}
            >
                {(title || showCloseButton) && (
                    <div className="px-4 py-3 border-b flex items-center justify-between">
                        <h3 id={ariaLabelledby} className="text-sm font-medium">
                            {title}
                        </h3>
                        {showCloseButton && (
                            <button
                                type="button"
                                onClick={onClose}
                                className="rounded p-1 text-gray-500 hover:bg-gray-100"
                                aria-label="Close dialog"
                            >
                                ✕
                            </button>
                        )}
                    </div>
                )}

                {/* Body: 높이/가로 스크롤 허용 */}
                <div className="p-4 overflow-auto overflow-x-auto">{children}</div>

                {footer && <div className="px-4 py-3 border-t">{footer}</div>}
            </div>
        </div>,
        document.body,
    );
}
